import { useState } from 'react';
import { rankItems } from '../data';
import { SectionHeader } from './SectionHeader';

const hotStyle: Record<string, string> = {
  爆: 'bg-primary-500 text-background-50',
  热: 'bg-primary-100 text-primary-700',
  新: 'bg-accent-100 text-accent-700',
  议: 'bg-secondary-100 text-secondary-900',
};

function DeltaIcon({ delta }: { delta: string }) {
  if (delta === 'new') {
    return <span className="text-xs font-semibold text-accent-600">新</span>;
  }
  if (delta === 'up') return <i className="ri-arrow-up-line text-xs text-primary-500"></i>;
  if (delta === 'down') return <i className="ri-arrow-down-line text-xs text-secondary-400"></i>;
  return <i className="ri-subtract-line text-xs text-secondary-400"></i>;
}

export default function HotRank() {
  const [active, setActive] = useState(0);

  return (
    <section id="rank" className="bg-background-100/60">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <SectionHeader
          kicker="全网热度排行"
          title="今日实时热点榜"
          desc="以跨平台讨论热度与发酵速度综合排序，越靠前代表此刻越多人正在围观，榜单随更新条滚动刷新。"
        />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="rounded-2xl bg-background-50 p-5 md:p-6 lg:col-span-2">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                <i className="ri-fire-line text-primary-500"></i>当前热度 · Top 10
              </div>
              <span className="text-xs text-foreground-500">约 3 分钟前更新</span>
            </div>
            <ol className="divide-y divide-background-100">
              {rankItems.map((item, idx) => (
                <li key={item.rank}>
                  <a
                    href="#feed"
                    className="group flex items-center gap-3 py-2.5 md:gap-4"
                    onMouseEnter={() => setActive(idx)}
                  >
                    <span
                      className={`w-6 shrink-0 text-center font-heading text-lg font-bold ${
                        idx < 3 ? 'text-primary-500' : 'text-secondary-400'
                      }`}
                    >
                      {String(item.rank).padStart(2, '0')}
                    </span>
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] font-bold ${
                        hotStyle[item.hot] ?? hotStyle['热']
                      }`}
                    >
                      {item.hot}
                    </span>
                    <span className="flex-1 truncate text-sm text-foreground-800 transition-colors group-hover:text-primary-600 md:text-[15px]">
                      {item.title}
                    </span>
                    <span className="flex w-8 shrink-0 items-center justify-center">
                      <DeltaIcon delta={item.delta} />
                    </span>
                    <span className="hidden w-16 shrink-0 justify-end text-xs tabular-nums text-secondary-500 sm:flex">
                      {item.trend.toLocaleString()}
                    </span>
                  </a>
                </li>
              ))}
            </ol>
            <div className="mt-4 flex items-center gap-2 rounded-xl bg-background-100 p-3 text-xs text-foreground-600 md:text-sm">
              <i className="ri-information-line text-secondary-500"></i>
              <span>热度榜仅为公开信息的聚合排序，用于快速判断关注度，不代表任何站方立场。</span>
            </div>
          </div>

          <div className="flex flex-col gap-5">
            <div className="rounded-2xl bg-accent-500 p-5 text-background-50 md:p-6">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <i className="ri-focus-3-line"></i>正在围观 · {active + 1}
              </div>
              <h3 className="font-heading mt-3 text-lg font-bold leading-snug">
                {rankItems[active].title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-background-50/90">
                当前热度 {rankItems[active].trend.toLocaleString()}，与上一轮相比处于持续发酵阶段，相关讨论仍在多个平台扩散。
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-50/95 px-4 py-2 text-sm font-semibold text-accent-700">
                <i className="ri-eye-line"></i>围观讨论
              </span>
            </div>
            <div className="flex-1 rounded-2xl border border-secondary-200 bg-background-50 p-5 md:p-6">
              <p className="mb-3 text-sm font-semibold text-foreground-900">怎么快速判断该看哪个？</p>
              <ul className="space-y-2.5 text-sm text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-time-line mt-0.5 text-primary-500"></i>
                  看「爆发速度」而非绝对量，帮你判断热度是刚起还是已过峰。
                </li>
                <li className="flex gap-2">
                  <i className="ri-user-heart-line mt-0.5 text-accent-600"></i>
                  关注与你相关领域的栏目，别被全网情绪带着跑。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-double-line mt-0.5 text-secondary-500"></i>
                  先信源、再判断，吃瓜也要有底线。
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}